import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Share2 } from "lucide-react";
import { useSeo } from "@/lib/seo";
import api from "@/lib/api";
import { WaveDivider } from "@/components/decor";
import SuggestPlace from "@/components/SuggestPlace";

const CATEGORIES = ["All", "Chewie & Solo", "Chewie & Solo's Favourite Places", "Behind the Books", "Bulldog Life", "New Releases", "The Happy Bulldog Co.", "Competitions", "Behind the Scenes"];

export default function Adventures() {
  useSeo("The Adventures — Chewie & Solo Blog",
    "Stories, behind-the-scenes and Bulldog life from The Happy Bulldog Co. and the world of Chewie & Solo.");
  const [active, setActive] = useState("All");
  const [all, setAll] = useState(null);

  useEffect(() => {
    api.get("/blog").then(({ data }) => setAll(data.posts)).catch(() => setAll([]));
  }, []);

  const posts = (all || []).filter((p) => active === "All" || p.category === active);

  return (
    <div>
      <section className="relative bg-[#A78BFA]/20 py-14 sm:py-20 text-center overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <span className="font-hand text-2xl text-[#FF6B9D]">Stories & mischief</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-6xl text-[#19324A] mt-1">THE ADVENTURES</h1>
          <p className="mt-4 text-[#19324A]/75 text-lg">Behind the books, bulldog life and everything in between.</p>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setActive(c)} data-testid={`adv-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-display font-semibold border-2 transition-colors ${active === c ? "bg-[#A78BFA] border-[#A78BFA] text-white" : "bg-white border-[#19324A]/10 text-[#19324A]/70 hover:border-[#A78BFA]"}`}>{c}</button>
          ))}
        </div>
        {active === "Chewie & Solo's Favourite Places" && (
          <Link to="/places" className="inline-flex items-center gap-2 rounded-full bg-[#19324A] text-white text-sm font-bold px-5 py-2.5 mb-6 hover:bg-[#FF6B9D] transition-colors" data-testid="adv-places-map-link">🗺️ See every place on the map</Link>
        )}
        {all === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[0, 1, 2].map((k) => <div key={k} className="h-72 rounded-3xl bg-white/60 animate-pulse" />)}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="adventure-grid">
            {posts.map((p) => (
              <Link to={`/adventures/${p.slug}`} key={p.id} className="block bg-white rounded-3xl overflow-hidden soft-shadow card-hover" data-testid={`post-card-${p.slug}`}>
                <div className="aspect-[16/10] overflow-hidden bg-[#FFF9EE]"><img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-5">
                  <span className="text-xs font-bold text-[#A78BFA] uppercase tracking-wide">{p.category}</span>
                  <h3 className="font-display font-bold text-lg text-[#19324A] mt-1 leading-snug">{p.title}</h3>
                  <p className="text-[#19324A]/65 text-sm mt-2 line-clamp-3">{p.excerpt}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[#FF6B9D] font-semibold text-sm inline-block">Read more →</span>
                    {p.share_count > 0 && <span className="text-xs text-[#19324A]/50 inline-flex items-center gap-1" data-testid={`post-shares-${p.slug}`}><Share2 className="w-3 h-3" /> Shared {p.share_count}×</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {all !== null && posts.length === 0 && <p className="text-center text-[#19324A]/50 py-10">No adventures in this category yet — check back soon! 🐾</p>}
        <p className="text-center text-sm text-[#19324A]/50 mt-10">More adventures are being written. Check back soon! 🐾</p>
      </section>
      <SuggestPlace />
    </div>
  );
}
